// The Console has one notification slot, not a queue. When several widgets fail
// together — an expired token, a backend outage — each card's toast overwrites the
// last and the user sees one arbitrary widget named. This gathers a burst into one
// message that says how many failed, and which.
//
// **The caller decides when the burst is over**, by calling `flush()`. This file has
// no idea what "over" means and does not subscribe to anything to find out. The
// original fixed window assumed a burst arrived in milliseconds; on a dashboard it
// does not, because queries run three at a time and each is an agent run, so a short
// timer fired once per wave and each toast overwrote the last.
//
// `windowMs` is the fallback for a caller that does not drive `flush`, and the
// backstop for one that does but never gets the chance. A caller that drives it
// should pass QUIESCENCE_BACKSTOP_MS, not the default.
//
// The caller owns the lifecycle throughout: hold the batcher somewhere stable (a ref)
// so a re-render cannot replace it mid-burst, and call `cancel()` on unmount so a
// toast scheduled on Insights never lands on an unrelated page.

const MAX_NAMED = 3;

// For a caller that drives `flush` itself, the timer is only a backstop against a
// query that never settles, so it should be far longer than a load. Left at 400ms it
// won every race: the timeline showed one toast per wave of three, each ~480ms after
// that wave finished, which is the bug batching exists to prevent.
export const QUIESCENCE_BACKSTOP_MS = 30000;

export function createFailureBatcher(notify, windowMs = 400) {
  let pending = [];
  let timer = null;

  const flush = () => {
    const batch = pending;
    pending = [];
    if (timer) clearTimeout(timer);
    timer = null;
    if (batch.length === 0) return;
    // One failure gets the whole story: there is room for it, and for a
    // non-retryable result the backend's sentence is the only thing that names a
    // remedy.
    if (batch.length === 1) {
      notify(batch[0].full, 'error');
      return;
    }

    const names = batch.slice(0, MAX_NAMED).map((b) => b.name).join(' · ');
    const rest = batch.length - MAX_NAMED;
    const more = rest > 0 ? ` · and ${rest} more` : '';

    // **When they all failed the same way, say it once.** That is the ordinary case,
    // not the exception: an expired token or a backend outage takes the whole
    // dashboard down at once, so every card reports the same reason and repeating it
    // per widget filled the toast with three copies of one phrase.
    const reasons = new Set(batch.map((b) => b.reason));
    if (reasons.size === 1) {
      notify(`${batch.length} widgets failed (${batch[0].reason}) — ${names}${more}`, 'error');
      return;
    }

    // Genuinely different reasons, so each name carries its own. Names only, never
    // the full sentences: three reviewed sentences (~300 chars each) produced a
    // ~430-character toast in which the count -- the one thing that matters when
    // everything fails at once -- was unfindable.
    const labelled = batch.slice(0, MAX_NAMED).map((b) => `${b.name}: ${b.reason}`).join(' · ');
    notify(`${batch.length} widgets failed — ${labelled}${more}`, 'error');
  };

  /**
   * @param {string} name    the widget's title, for listing it among others
   * @param {string} reason  short, e.g. "couldn't load" — hoisted out when every
   *                         failure in the batch shares it
   * @param {string} [full]  the whole message, used when this turns out to be the
   *                         only failure. Defaults to "name: reason".
   */
  const report = (name, reason, full) => {
    pending.push({ name, reason, full: full ?? `${name}: ${reason}` });
    if (!timer) timer = setTimeout(flush, windowMs);
  };

  const cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    pending = [];
  };

  // `flush` is exposed so the CALLER can drive it from whatever means "the load is
  // over" — it does not subscribe to anything itself. An earlier version did, and
  // unsubscribed inside `cancel()`; under React's double-mount that cleanup ran once
  // and the batcher was never re-subscribed, so only the timer ever fired it. The
  // caller already owns this lifecycle for the same reason.
  return { report, flush, cancel };
}
